/**
 * @fileoverview State and County selectors for the department details
 *   slide-in, backed by the shared lookup system (the same `listLookups`
 *   function the geo cache upload system and onboarding wizard use). Replaces
 *   the previous free-text State/County inputs so jurisdiction values carry
 *   the lookup `code` and the county FIPS needed for parcel-data auto-match.
 *
 *   Each field (State, County) follows the same display/edit toggle pattern as
 *   `InlineEditableField`: the value is shown as read-only text with a dashed
 *   underline and pencil indicator. Clicking the field transitions it into
 *   edit mode, which renders the cascading dropdown. Selecting a value saves
 *   immediately (optimistic local-first write) and returns to display mode.
 * @module system-web/components/JurisdictionStateCountyField
 *
 * Responsibilities:
 * - Render State and County as display text with click-to-edit behavior
 *   (matching the InlineEditableField pattern used by all other fields).
 * - On edit: render State and County as cascading dropdowns (county list loads
 *   for the selected state).
 * - On state change: store a LookupRef on `jurisdiction.state`, clear
 *   `jurisdiction.county` and `jurisdiction.fipsCode`, and persist.
 * - On county change: store a LookupRef on `jurisdiction.county`, resolve
 *   `jurisdiction.fipsCode` from the county lookup's `config.fipsCode`/`code`,
 *   and persist.
 * - Persist the full updated jurisdiction object through the provided
 *   `onSave` callback (single save, single source of truth).
 *
 * Dependencies:
 * - ../hooks/useJurisdictionLookups (state/county lookup loading).
 * - ../utils/jurisdictionFips (resolveCountyFips, toLookupRef, LookupRef).
 * - lucide-react (Pencil icon for edit indicator).
 *
 * Architecture:
 * - Local-first. Reads lookup values from Dexie via useJurisdictionLookups.
 * - No direct API calls; persistence is delegated to the parent's `onSave`.
 * - Presentation-only with respect to data: it computes the next jurisdiction
 *   object and hands it to the parent, which writes it through the local-first
 *   tenant service.
 *
 * Notes:
 * - The parent save handler is expected to derive `address.state` and
 *   `address.county` from the saved jurisdiction so the Address section stays
 *   consistent without a duplicate editable copy of the same data.
 * - No mock data, no hardcoded URLs.
 *
 * @component JurisdictionStateCountyField
 *
 * Purpose:
 * - Provide lookup-backed State/County dropdowns for the department details
 *   slide-in, used in both the Address and Jurisdiction sections, with the
 *   same display/edit toggle UX as InlineEditableField.
 *
 * Props:
 * - jurisdiction: The tenant's current jurisdiction object
 *   ({ state?, county?, fipsCode?, ... }).
 * - onSave: Callback receiving the full next jurisdiction object. The parent
 *   persists it (and derives address.state/county).
 * - showLabels: When true, render field labels above the dropdowns (default true).
 * - disabled: Disable both dropdowns.
 *
 * Behavior:
 * - Display mode: State/County shown as text with dashed underline + pencil.
 * - Click a field to enter edit mode → cascading dropdown appears.
 * - State dropdown lists all states; selecting one clears county + fipsCode.
 * - County dropdown lists counties for the selected state; selecting one
 *   resolves fipsCode.
 * - Each change calls onSave immediately (optimistic local-first write) and
 *   exits edit mode.
 * - Click-away exits edit mode without saving.
 *
 * Data Source:
 * - local (Dexie via useJurisdictionLookups hook).
 *
 * Constraints:
 * - No direct API calls; all mutations via the parent onSave callback.
 * - No writes inside a liveQuery (the hook loads in effects, not in liveQuery).
 */
import { useCallback, useState, useRef, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { useJurisdictionLookups } from '../hooks/useJurisdictionLookups';
import { resolveCountyFips, toLookupRef, type LookupRef } from '../utils/jurisdictionFips';

export interface JurisdictionStateCountyFieldProps {
  /** The tenant's current jurisdiction object. */
  jurisdiction: Record<string, unknown>;
  /** Persist the full next jurisdiction object. */
  onSave: (nextJurisdiction: Record<string, unknown>) => void;
  /** Render field labels above the dropdowns (default true). */
  showLabels?: boolean;
  /** Disable both dropdowns. */
  disabled?: boolean;
}

const SELECT_CLS =
  'w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors disabled:bg-gray-50 disabled:text-gray-400';
const LABEL_CLS = 'text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5';

export default function JurisdictionStateCountyField({
  jurisdiction,
  onSave,
  showLabels = true,
  disabled = false,
}: JurisdictionStateCountyFieldProps) {
  const stateRef = (jurisdiction.state as LookupRef | undefined) ?? undefined;
  const countyRef = (jurisdiction.county as LookupRef | undefined) ?? undefined;
  const existingFips = typeof jurisdiction.fipsCode === 'string' ? (jurisdiction.fipsCode as string) : undefined;

  const { states, counties, loadingStates, loadingCounties, getCountyItem } =
    useJurisdictionLookups(stateRef?.code);

  // ── Edit mode state (per-field, matching InlineEditableField pattern) ──
  const [editingState, setEditingState] = useState(false);
  const [editingCounty, setEditingCounty] = useState(false);
  const stateWrapperRef = useRef<HTMLDivElement>(null);
  const countyWrapperRef = useRef<HTMLDivElement>(null);

  const handleStateChange = useCallback(
    (code: string) => {
      const item = states.find((s) => s.code === code);
      const ref = toLookupRef(item as Record<string, any> | undefined);
      const next: Record<string, unknown> = {
        ...jurisdiction,
        state: ref,
        // Clearing county + fipsCode when the state changes keeps the
        // jurisdiction internally consistent (county must belong to state).
        county: undefined,
        fipsCode: undefined,
      };
      onSave(next);
      setEditingState(false);
    },
    [states, jurisdiction, onSave],
  );

  const handleCountyChange = useCallback(
    (code: string) => {
      const item = counties.find((c) => c.code === code);
      const ref = toLookupRef(item as Record<string, any> | undefined);
      const fips = resolveCountyFips(ref, item as Record<string, any> | undefined, existingFips, false);
      const next: Record<string, unknown> = {
        ...jurisdiction,
        county: ref,
        fipsCode: fips,
      };
      onSave(next);
      setEditingCounty(false);
    },
    [counties, jurisdiction, existingFips, onSave],
  );

  // ── Click-away: exit edit mode when clicking outside the active editor ──
  useEffect(() => {
    if (!editingState && !editingCounty) return;
    const handler = (e: MouseEvent) => {
      if (editingState && stateWrapperRef.current && !stateWrapperRef.current.contains(e.target as Node)) {
        setEditingState(false);
      }
      if (editingCounty && countyWrapperRef.current && !countyWrapperRef.current.contains(e.target as Node)) {
        setEditingCounty(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editingState, editingCounty]);

  const stateDisplay = stateRef?.name || stateRef?.code || '';
  const countyDisplay = countyRef?.name || countyRef?.code || '';

  const enterStateEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setEditingState(true);
  }, [disabled]);

  const enterCountyEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setEditingCounty(true);
  }, [disabled]);

  return (
    <>
      <div>
        {showLabels && <div className={LABEL_CLS}>State</div>}
        {editingState ? (
          <div ref={stateWrapperRef} onClick={(e) => e.stopPropagation()}>
            <select
              value={stateRef?.code ?? ''}
              onChange={(e) => handleStateChange(e.target.value)}
              disabled={disabled || loadingStates}
              className={SELECT_CLS}
              aria-label="State"
              title="State"
              autoFocus
            >
              <option value="">{loadingStates ? 'Loading…' : 'Select state'}</option>
              {states.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div
            ref={stateWrapperRef}
            onClick={enterStateEdit}
            className="group/jsc relative inline-flex items-center gap-1 w-full cursor-text"
            title="Click to edit"
          >
            <span className="text-sm text-gray-800 border-b border-dashed border-gray-300 group-hover/jsc:border-solid group-hover/jsc:border-orange-400 transition-colors pb-px">
              {stateDisplay || <span className="text-gray-400">—</span>}
            </span>
            <Pencil className="h-3 w-3 text-gray-300 opacity-25 group-hover/jsc:opacity-70 group-hover/jsc:text-orange-500 transition-all shrink-0" />
          </div>
        )}
      </div>
      <div>
        {showLabels && <div className={LABEL_CLS}>County</div>}
        {editingCounty ? (
          <div ref={countyWrapperRef} onClick={(e) => e.stopPropagation()}>
            <select
              value={countyRef?.code ?? ''}
              onChange={(e) => handleCountyChange(e.target.value)}
              disabled={disabled || !stateRef?.code || loadingCounties}
              className={SELECT_CLS}
              aria-label="County"
              title="County"
              autoFocus
            >
              <option value="">
                {!stateRef?.code ? 'Select state first' : loadingCounties ? 'Loading…' : 'Select county'}
              </option>
              {counties.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div
            ref={countyWrapperRef}
            onClick={enterCountyEdit}
            className="group/jsc relative inline-flex items-center gap-1 w-full cursor-text"
            title="Click to edit"
          >
            <span className="text-sm text-gray-800 border-b border-dashed border-gray-300 group-hover/jsc:border-solid group-hover/jsc:border-orange-400 transition-colors pb-px">
              {countyDisplay || <span className="text-gray-400">—</span>}
            </span>
            <Pencil className="h-3 w-3 text-gray-300 opacity-25 group-hover/jsc:opacity-70 group-hover/jsc:text-orange-500 transition-all shrink-0" />
          </div>
        )}
      </div>
    </>
  );
}
